import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type {
  FranklinContextPayload,
  FranklinTab,
  FranklinThemeConfig,
} from "../types";

type FranklinContextState = {
  isOpen: boolean;
  activeTab: FranklinTab;
  context: FranklinContextPayload;
  theme: FranklinThemeConfig;
  orgId: string;
  sdkKey: string;
  apiEndpoint: string;
};

type FranklinContextValue = FranklinContextState & {
  open: () => void;
  close: () => void;
  toggle: () => void;
  setActiveTab: (tab: FranklinTab) => void;
  pushContext: (payload: FranklinContextPayload) => void;
};

const FranklinContext = createContext<FranklinContextValue | undefined>(
  undefined
);

type FranklinProviderProps = {
  children: ReactNode;
  orgId: string;
  sdkKey: string;
  defaultTab?: FranklinTab;
  theme?: FranklinThemeConfig;
  apiEndpoint?: string;
  onOpen?: () => void;
  onClose?: () => void;
};

export function FranklinProvider({
  children,
  orgId,
  sdkKey,
  defaultTab = "ai",
  theme = {},
  apiEndpoint = "/api/franklin",
  onOpen,
  onClose,
}: FranklinProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTabState] = useState<FranklinTab>(defaultTab);
  const [context, setContext] = useState<FranklinContextPayload>({});

  const open = useCallback(() => {
    setIsOpen(true);
    onOpen?.();
  }, [onOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  const setActiveTab = useCallback((tab: FranklinTab) => {
    setActiveTabState(tab);
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("franklin:tab-change", {
          detail: { tab },
        })
      );
    }
  }, []);

  const pushContext = useCallback((payload: FranklinContextPayload) => {
    setContext((prev) => ({
      ...prev,
      ...payload,
      metadata:
        payload.metadata || prev.metadata
          ? {
              ...(prev.metadata ?? {}),
              ...(payload.metadata ?? {}),
            }
          : undefined,
    }));
  }, []);

  const value: FranklinContextValue = {
    isOpen,
    activeTab,
    context,
    theme,
    orgId,
    sdkKey,
    apiEndpoint,
    open,
    close,
    toggle,
    setActiveTab,
    pushContext,
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("franklin:tab-change", {
          detail: { tab: defaultTab },
        })
      );
    }
  }, [defaultTab]);

  return (
    <FranklinContext.Provider value={value}>
      {children}
    </FranklinContext.Provider>
  );
}

export function useFranklinContext() {
  const context = useContext(FranklinContext);
  if (context === undefined) {
    throw new Error(
      "useFranklinContext must be used within a FranklinProvider"
    );
  }
  return context;
}
